import { motion, useInView } from 'framer-motion';
import type { ReactNode } from 'react';
import { useRef } from 'react';

import { EASE_OUT, MATRIX_GREEN, MATRIX_GREEN_DIM, MATRIX_RED, MATRIX_TERMINAL_BG } from './constants';

export function MatrixSection({ children, className = '', delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }} transition={{ duration: 0.45, delay, ease: EASE_OUT }} className={className}>
      {children}
    </motion.div>
  );
}

export function TerminalLine({ label, value, delay = 0 }: { label: string; value: string; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });

  return (
    <motion.div ref={ref} initial={{ opacity: 0, x: -20 }} animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }} transition={{ duration: 0.4, delay, ease: EASE_OUT }} className='flex items-baseline gap-2 py-2 font-mono text-sm md:text-base' style={{ fontFamily: 'var(--font-jetbrains), monospace' }}>
      <span style={{ color: MATRIX_GREEN }}>{'>'}</span>
      <span style={{ color: MATRIX_GREEN_DIM }}>{label}:</span>
      <span className='font-bold' style={{ color: MATRIX_GREEN }}>
        {value}
      </span>
    </motion.div>
  );
}

export function InterceptedTransmission({ name, title, game, quote, delay = 0 }: { name: string; title: string; game: string; quote: string; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });

  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }} transition={{ duration: 0.45, delay, ease: EASE_OUT }} className='rounded-lg border p-5 md:p-6' style={{ backgroundColor: MATRIX_TERMINAL_BG, borderColor: `${MATRIX_GREEN}20` }}>
      <div className='mb-3 flex items-center gap-2 font-mono text-[10px] md:text-xs' style={{ fontFamily: 'var(--font-jetbrains), monospace', color: MATRIX_GREEN_DIM }}>
        <span style={{ color: MATRIX_RED }}>&#47;&#47;&#47;</span> INTERCEPTED TRANSMISSION <span style={{ color: MATRIX_GREEN }}>[{game.toUpperCase()}]</span>
      </div>

      <p className='mb-4 text-base italic leading-relaxed md:text-lg' style={{ fontFamily: 'var(--font-bai)', color: '#E5E7EB' }}>
        &ldquo;{quote}&rdquo;
      </p>

      <div className='flex items-center gap-2 font-mono text-xs' style={{ fontFamily: 'var(--font-jetbrains), monospace' }}>
        <span style={{ color: MATRIX_GREEN }}>{'>'}</span>
        <span style={{ color: MATRIX_GREEN }} className='font-bold'>
          {name}
        </span>
        <span style={{ color: MATRIX_GREEN_DIM }}>&#47;&#47; {title}</span>
      </div>
    </motion.div>
  );
}

export function SectionTitle({ title, terminalLine, accentWord }: { title: string; terminalLine: string; accentWord?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });

  let titleNode: ReactNode = title;
  if (accentWord) {
    const idx = title.toLowerCase().indexOf(accentWord.toLowerCase());
    if (idx !== -1) {
      const before = title.slice(0, idx);
      const matched = title.slice(idx, idx + accentWord.length);
      const after = title.slice(idx + accentWord.length);
      titleNode = (
        <>
          {before}
          <span style={{ color: MATRIX_GREEN }}>{matched}</span>
          {after}
        </>
      );
    }
  }

  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }} transition={{ duration: 0.5, ease: EASE_OUT }} className='text-center'>
      <h2 className='mb-4 text-2xl font-bold md:text-3xl lg:text-4xl' style={{ fontFamily: 'var(--font-bai)', color: '#FFFFFF' }}>
        {titleNode}
      </h2>
      <p className='mb-14 font-mono text-xs md:text-sm' style={{ fontFamily: 'var(--font-jetbrains), monospace', color: MATRIX_GREEN_DIM }}>
        {'>'} {terminalLine}
      </p>
    </motion.div>
  );
}
